import { ChangeDetectionStrategy, Component, TemplateRef, computed, contentChild, input } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { partitionIntoColumns, type BalanceablePhoto } from '@lite-workspace/shared/blocks/gallery-render';

// Tile-agnostic masonry. The grid owns ONLY the column geometry: the count and
// the gap come from the caller (its own design model — see MASONRY_COLUMNS /
// MASONRY_GAP), the tile is projected as an <ng-template> so no card type ever
// crosses into this lib.
//
// The render is N explicit column boxes in a flex row, each `flex-1` so the run
// always spans the FULL container width. CSS multi-column was tried and lost:
// `column-fill: balance` guesses one target height from the total, then fills
// greedily, and `break-inside-avoid` tiles that overshoot that guess are pushed
// into the earlier columns — with tall portraits the items ran out before the
// last column, leaving dead space on the right (FKS-12732).
//
// ⚠️ Lockstep: the column split comes from the SAME packer the caller uses to
// flatten its lightbox order (`balanceMasonryOrder` = these columns
// concatenated) — a click must open the photo the viewer then pages from, so
// both sides must feed the packer the identical columnCount. Hence columnCount
// is a REQUIRED input: the grid never sniffs its geometry back off the DOM.
@Component({
  selector: 'fcr-masonry-grid',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
  host: { class: 'block' },
  template: `
    <div class="flex items-start" [style.gap.px]="gapPx()">
      @for (column of columns(); track $index) {
        <div class="flex min-w-0 flex-1 flex-col" [style.gap.px]="gapPx()">
          @for (item of column; track item.id) {
            <ng-container [ngTemplateOutlet]="tile()" [ngTemplateOutletContext]="{ $implicit: item }" />
          }
        </div>
      }
    </div>
  `,
})
export class MasonryGridComponent<T extends BalanceablePhoto> {
  readonly items = input.required<readonly T[]>();
  readonly columnCount = input.required<number>();
  readonly gapPx = input.required<number>();

  readonly tile = contentChild.required(TemplateRef);

  protected readonly columns = computed(() => {
    const count = this.columnCount();
    return partitionIntoColumns(this.items(), Number.isFinite(count) ? Math.max(1, Math.floor(count)) : 1).map(c => c.items);
  });
}
